import "../assets/styles/chatSection.scss"
import React, { useState, useRef, useEffect } from "react";
import { message, Modal, Radio, Spin } from "antd";
import UserIcon from "../assets/images/27470347_7309691 1.svg";
import BotIcon from "../assets/images/1100838311-preview 1.svg";
import { useDispatch } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import "perfect-scrollbar/css/perfect-scrollbar.css";
import { chatBotTemplateAPI } from "../apis";
import { FilePdfOutlined, StarOutlined } from "@ant-design/icons";
import TextArea from "antd/es/input/TextArea";
import { Auth } from "../libs/utilities/helpers/Auth";
import mammoth from 'mammoth';
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

const ChatSection = ({ inputValue, selectedFile, folderList, feature, setChatLoad, serviceId }) => {
  const dispatch = useDispatch();
  const [chatHistory, setChatHistory] = useState([]);
  const chatContentRef = useRef(null);
  const messagesRef = useRef([]);
  const [selectedPDF, setSelectedPDF] = useState("");
  const [pdfLoading, setPDFLoading] = useState(false);
  const [feedbackGiven, setFeedbackGiven] = useState({});
  const [ratings, setRatings] = useState({});
  const [dislikeModal, setDislikeModal] = useState({
    visible: false,
    messageId: null,
    starRating: null,
  });
  const [selectedOption, setSelectedOption] = useState(null);
  const [dislikeFeedback, setDislikeFeedback] = useState("");
  const dislikeInputRef = useRef(null);
  const user = Auth.getUser();

  //useEffect to adjust scroll to top and bottom
  useEffect(() => {
    const lastMessage = messagesRef.current[chatHistory.length - 1];
    if (chatContentRef.current && lastMessage) {
      const chatBox = chatContentRef.current;
      const lastMessageRect = lastMessage.getBoundingClientRect();
      const chatBoxRect = chatBox.getBoundingClientRect();
      // Check if last message is not fully visible
      if (lastMessageRect.bottom > chatBoxRect.bottom) {
        lastMessage.scrollIntoView({
          behavior: "smooth",
          block: "start", // Scroll just enough to show the message
        });
      }
    }
  }, [chatHistory]);

  //useEffect to take a input value from the gpt template and render the response
  useEffect(() => {
    if (inputValue) {
      handleChatSubmit(inputValue);
    }
  }, [inputValue])

  const getFileName = (fileId) => {
    const file = folderList ?.find(item => item.file ?.find(file => file ?.id === fileId)) ?.file ?.find(item => item.id === fileId);
    return file ?.name;
  }

  //complete logic of the response render and all validations
  const handleChatSubmit = async (selectedMessage) => {
    const strippedText = selectedMessage.trim();
    const cleanedInput = new DOMParser().parseFromString(strippedText, 'text/html').body.textContent.trim();
    const HTML_TAG_REGEX = /<[^>]+>/;
    const SCRIPT_TAG_REGEX = /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/i;
    const lastMessageId = chatHistory[chatHistory.length - 1] ?.messageId;

    // Check if feedback is required and hasn't been given for the last query
    if (lastMessageId && !feedbackGiven[lastMessageId] && feature.feedbackMandatory) {
      message.error("Please provide feedback for the previous query before submitting a new one.");
      return;
    }

    if (!cleanedInput || cleanedInput.length > 300 || SCRIPT_TAG_REGEX.test(cleanedInput) || HTML_TAG_REGEX.test(cleanedInput)) {
      message.destroy();
      if (!cleanedInput) {
        message.error("Prompt can not be empty.");
        setChatLoad(false);
      } else if (cleanedInput.length > 300) {
        message.error("The prompt must be limited to a maximum of 300 characters.");
        setChatLoad(false);
      } else if (SCRIPT_TAG_REGEX.test(cleanedInput)) {
        message.error("Invalid input");
        setChatLoad(false);
      } else if (HTML_TAG_REGEX.test(cleanedInput)) {
        message.error("Please enter a valid prompt without special characters");
        setChatLoad(false);
      } 
      return;
    }

    if ((!selectedFile || selectedFile.length) === 0 && feature.uploadFile && feature.uploadAccessRole.includes(user.Role) && (
      feature.departmentUploadAccess.includes(user.Department) ||
      feature.departmentUploadAccess.includes('all')
    )) {
      message.error("Please select a file");
      setChatLoad(false);
    }
    else if (!selectedMessage.trim()) {
      message.error("Invalid selected message");
      setChatLoad(false);
    } else {
      setChatLoad(true);
      let uuid = uuidv4();
      let queryData = {
        type: "user",
        uuid: uuid,
        query: cleanedInput,
        response: null,
        files: [],
        isLoading: true,
        content: cleanedInput,
        responseType: "success",
      };
      setChatHistory((prevMessages) => [...prevMessages, queryData]);
      let allFileIds = [];
      
      if (!feature.uploadAccessRole.includes(user.Role)) {         
          allFileIds = folderList.reduce((acc, curr) => {
            if (curr.file.length) {
              acc.push(...curr.file.map(file => file.id))
            }
            return acc;
          }, [])
      }
      const requestBody = {
        query: cleanedInput,
        query_id: uuid,
        ...(feature.uploadFile && {
          file_id: feature.uploadAccessRole.includes(user.Role) && (
            feature.departmentUploadAccess.includes(user.Department) ||
            feature.departmentUploadAccess.includes('all')
          ) ? selectedFile : allFileIds
        }),
      };
      try {
        const response = await dispatch(
          chatBotTemplateAPI.generateAnswer({ serviceId, ...requestBody, })
        );
        if (response.error) {
          let query_id = response.meta.arg.query_id;
          setChatHistory((previousHistory) => {
            let updatedHistory = previousHistory;
            const targetIndex = updatedHistory.findIndex(
              (item) => item.uuid === query_id
            );
            updatedHistory[targetIndex].response = "Something went wrong!";
            updatedHistory[targetIndex].isLoading = false;
            updatedHistory[targetIndex].responseType = "error";
            return [...updatedHistory];
          });
        } else {
          const answer = response.payload.data.answer.replace(/^\n+/, ''); // Extract the answer
          const metaData = response.payload.data.meta; // Extract the meta string

          setChatHistory((previousHistory) => {
            let updatedHistory = previousHistory;
            const targetIndex = updatedHistory.findIndex(
              (item) => item.uuid === response.meta.arg.query_id
            );
            updatedHistory[targetIndex].response = answer;
            updatedHistory[targetIndex].meta = metaData;
            updatedHistory[targetIndex].isLoading = false;
            updatedHistory[targetIndex].responseType = "success";
            updatedHistory[targetIndex].messageId =
              response.payload.data.messageId;
            return [...updatedHistory];
          });
        }
      } catch {

      }
    }
  };

  const getMimeType = (ext) => {
    switch (ext) {
      case '.pdf': return 'application/pdf';
      case '.doc': return 'application/msword';
      case '.docx': return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      case '.txt': return 'text/plain';
      default: return 'application/octet-stream';
    }
  };

  const openInViewer = (blob, fileName, fileExtension, page) => {
    const url = URL.createObjectURL(blob);

    if (fileExtension === '.pdf') {
      window.open(`${url}#page=${page}`, "_blank");
    } else if (fileExtension === '.txt') {
      const newWindow = window.open();
      const reader = new FileReader();
      reader.onload = () => {
        newWindow.document.write(`<pre style="white-space: pre-wrap; font-family: monospace;">${reader.result}</pre>`);
      };
      reader.readAsText(blob);
    } else if (fileExtension === '.docx') {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const arrayBuffer = e.target.result;
        try {
          const result = await mammoth.convertToHtml({ arrayBuffer });
          const newWindow = window.open();
          newWindow.document.write(`
            <html>
              <head>
                <title>DOCX Preview</title>
                <style>
                  body {
                    padding: 20px;
                    font-family: sans-serif;
                    overflow-y: auto;
                  }
                  .page {
                    page-break-before: always;
                    margin-bottom: 20px;
                  }
                </style>
              </head>
              <body>
                <div id="docx-content">${result.value}</div>
                <script>
                  const pages = document.querySelectorAll('.page');
                  if (pages.length > 0) {
                    const pageIndex = ${page} - 1; // Adjusting for 0-based index
                    if (pages[pageIndex]) {
                      pages[pageIndex].scrollIntoView({ behavior: 'smooth' });
                    }
                  }
                </script>
              </body>
            </html>
          `);
          newWindow.document.close();
        } catch (err) {
          console.error("Failed to render DOCX file.", err);
          message.error("Failed to render DOCX file. Please download it manually.");
        }
      };
      reader.readAsArrayBuffer(blob);
    } else {
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const downloadPDF = async (name, fileId, page) => {
    setSelectedPDF(fileId);
    const folder = folderList.find(item => item ?.file ?.find(file => file.id === fileId));
    const file = folder ?.file ?.find(item => item ?.id === fileId);
    if (!file) return;
    const fileExtension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();

    if (!feature.fileFormat.includes(fileExtension) && feature ?.uploadFile) {
      message.error(`File format ${fileExtension} is not allowed.`);
      return;
    }
    const data = {
      folderName: folder ?.folder_name,
      fileName: file ?.name,
      fileId: fileId
    };
    setPDFLoading(true);
    const response = await dispatch(chatBotTemplateAPI.getPdf({ serviceId, ...data }));
    if (!response.error) {
      const binaryString = window.atob(response ?.payload ?.data);
      const binary = new Uint8Array(binaryString ?.length);
      for (let i = 0; i < binaryString.length; i++) {
        binary[i] = binaryString.charCodeAt(i);
      }
      const mimeType = getMimeType(fileExtension);
      const blob = new Blob([binary], { type: mimeType });
      openInViewer(blob, file.name, fileExtension, page);
    }
    setPDFLoading(false);
  };

  //logic for the feedback api
  const handleStarClick = async (starRating, messageId) => {
    if (feedbackGiven[messageId] ?.starRating >= 4) {
      return; // Prevent editing for 4-5 star feedback
    }
    setRatings((prev) => ({ ...prev, [messageId]: starRating }));
    if (starRating <= 3) {
      const existingFeedback = feedbackGiven[messageId];
      setDislikeModal({
        visible: true,
        messageId,
        starRating,
      });
      // Populate modal with existing feedback
      if (existingFeedback) {
        setSelectedOption(existingFeedback.reason || null);
        setDislikeFeedback(existingFeedback.comments || "");
      } else {
        setSelectedOption(null);
        setDislikeFeedback("");
      }
    } else {
      try {
        // Save feedback directly for ratings 4 and 5
        const feedbackData = { messageId, feedbackType: starRating };
        dispatch(chatBotTemplateAPI.saveFeedback({ serviceId, ...feedbackData }));
        setFeedbackGiven((prev) => ({
          ...prev,
          [messageId]: { starRating },
        }));
        message.success("Feedback submitted successfully!", 3);
      } catch (err) {
        //console.log(err, "err in feedback api");
      }
    }
  };

  const handleModalClose = () => {
    const existingFeedback = feedbackGiven[dislikeModal.messageId];
    if (existingFeedback) {
      // Restore previous feedback (if any)
      setRatings((prev) => ({
        ...prev,
        [dislikeModal.messageId]: existingFeedback.starRating,
      }));
      setSelectedOption(existingFeedback.reason || null);
      setDislikeFeedback(existingFeedback.comments || "");
    } else {
      // Reset the star rating to undefined (if no existing feedback)
      setRatings((prev) => ({
        ...prev,
        [dislikeModal.messageId]: undefined,
      }));
      setSelectedOption(null);
      setDislikeFeedback("");
    }
    // Close the modal
    setDislikeModal({ visible: false, messageId: null, starRating: null });
  };

  const handleModalSubmit = async () => {
    if (!selectedOption) {
      message.error("Please select a reason for your dislike.");
      return;
    }
    const feedbackMessage = `${selectedOption}${
      dislikeFeedback ? `. ${dislikeFeedback}` : ""
      }`;
    const feedbackData = {
      messageId: dislikeModal.messageId,
      feedback: feedbackMessage,
      feedbackType: dislikeModal ?.starRating
    };
    await dispatch(chatBotTemplateAPI.saveFeedback({ serviceId, ...feedbackData }));
    setFeedbackGiven((prev) => ({
      ...prev,
      [dislikeModal.messageId]: {
        starRating: dislikeModal.starRating,
        reason: selectedOption,
        comments: dislikeFeedback,
      },
    }));
    message.success("Feedback updated successfully!", 3);
    // Reset modal state
    setDislikeModal({ visible: false, messageId: null });
    setSelectedOption(null);
    setDislikeFeedback("");
  };

  const handleOptionChange = (e) => {
    setSelectedOption(e.target.value);
  };

  const handleDislikeInputChange = (e) => {
    setDislikeFeedback(e.target.value);
  };

  return (
    <>
      <div
        ref={chatContentRef}
        className="chat-main-section"
        style={styles.conversation}
      >
        {chatHistory.length > 0
          ? chatHistory.map((item, index) => {
            return (
              <div
                key={index}
                className="chat-main-part"
                ref={(el) => (messagesRef.current[index] = el)}
              >
                <div
                  className={`chat-message user`}
                  style={{ alignItems: "flex-start" }}
                >
                  <span className="user-message success">{item.query}</span>
                  <img
                    className="user-icon"
                    style={{ marginTop: "0" }}
                    src={UserIcon}
                    alt="user-icon"
                    width={40}
                    height={40}
                  />
                </div>
                <div
                  className={`chat-message bot`}
                  style={{ alignItems: "flex-start" }}

                >
                  <img
                    className="bot-icon"
                    src={BotIcon}
                    alt="bot-icon"
                    width="auto"
                    height={40}
                  />
                  {item.isLoading ? (
                    <Spin
                      size="small"
                     style={{marginLeft:8,marginTop:11}}
                    />
                  ) : (
                       <div className={`bot-message markdown-wrapper-gpt ${item ?.responseType}`}>
                         <Markdown
                            remarkPlugins={[remarkGfm]}
                            components={{
                              h3: ({ node, ...props }) => <h3 style={{ fontSize: "14px" }} {...props} />
                          }}
                          >{item?.response}</Markdown>
                        {feature ?.fileReference && (
                          <>
                            {
                              item ?.meta && (
                                <>
                                  {item ?.meta ?.length > 0 && (
                                    <div
                                    className="ai-generated-text"
                                    >
                                      This response is generated by Gen AI. Please
                                      verify the information by clicking on the given
                                      pdf link.
                              </div>
                                  )}

                                  <div
                                    className="meta-tag-div"
                                  >
                                    <div style={{ display: "flex", flexDirection: "column" }}>
                                      {item ?.meta ?.map((file, index) => {
                                        return (
                                          <div key={index} className="meta-details">
                                            <div className="meta-details-innerdiv">
                                              <div className="file-pageno" onClick={() => downloadPDF(getFileName(file ?.file_id), file ?.file_id, 0)}>
                                                <FilePdfOutlined />
                                                {getFileName(file ?.file_id)}
                                              </div>

                                              {file ?.page.length > 0 && (
                                                <div>
                                                  &nbsp;- (
                                            {file ?.page.map((page, index) => (
                                                    <span
                                                      key={index}
                                                      onClick={() =>
                                                        downloadPDF(
                                                          getFileName(file ?.file_id),
                                                          file ?.file_id,
                                                          page
                                                        )
                                                      }
                                                      className="meta-pages-no"
                                                    >
                                                      Page no: {page}
                                                      {index !==
                                                        file ?.page.length - 1 && ", "}
                                                    </span>
                                                  ))}
                                                  )
                                          </div>
                                              )}
                                            </div>
                                            {selectedPDF === file ?.file_id &&
                                              pdfLoading && <div className="text-loading" >Loading...</div>}
                                          </div>
                                        )
                                      })}
                                    </div>
                                  </div>
                                </>
                              )
                          }
                          </>
                        )
                      }
                        {
                          // feature.feedbackMandatory &&
                          <div>
                            {item.responseType !== "error" && (
                              <div
                                className="star-rating"
                                style={{
                                  display: "flex",
                                  gap: "2px",
                                  float: "right",
                                  marginRight: "10px",
                                  alignItems: "end",
                                }}
                              >
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <span
                                    key={star}
                                    onClick={() =>
                                      feedbackGiven[item.messageId]
                                        ?.starRating >= 4
                                          ? null
                                          : handleStarClick(
                                            star,
                                            item.messageId
                                          )
                                      } // Block interaction for 4-5 ratings
                                    className={`star ${
                                      ratings[item.messageId] >= star
                                        ? "filled"
                                        : ""
                                      }`}
                                    style={{
                                      cursor:
                                        feedbackGiven[item.messageId]
                                          ?.starRating >= 4
                                            ? "not-allowed"
                                            : "pointer", // Show blocked cursor for 4-5
                                    }}
                                  >
                                    <StarOutlined />
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        }
                      </div>
                    )}
                </div>
              </div>

            )
          })
          : null}
      </div>

      <Modal
        title="Tell us more, why did you dislike?"
        open={dislikeModal.visible}
        onCancel={handleModalClose}
        onOk={handleModalSubmit}
        okText="Submit"
        closable={false} // Removes the close icon
        maskClosable={false} // Prevents closing when clicking outside the modal
        okButtonProps={{
          disabled: !selectedOption, // Enable the submit button only if a radio button is selected
        }}
      >
        <Radio.Group onChange={handleOptionChange} value={selectedOption}>
          <Radio value="Relevant but not exhaustive">
            Relevant but not exhaustive
          </Radio>
          <Radio value="Slightly irrelevant">Slightly irrelevant </Radio>
          <Radio value="Completely irrelevant">Completely irrelevant</Radio>
        </Radio.Group>
        <TextArea
          ref={dislikeInputRef}
          placeholder="Please provide additional comments (optional)"
          value={dislikeFeedback}
          onChange={handleDislikeInputChange}
          autoFocus
          style={{
            height: "6rem",
            borderRadius: "5px",
            padding: "0.5rem",
            fontSize: "14px",
            boxSizing: "border-box",
            lineHeight: "1.25",
            verticalAlign: "top",
            display: "block",
            width: "100%",
            resize: "none",
          }}
          maxLength={1000}
        />
      </Modal>
    </>
  );
};

export default ChatSection;

const styles = {
  conversation: {
    flex: 1,
    overflowY: "overlay",
    // maxWidth: "923px",
    marginLeft: "auto",
    marginRight: "auto",
    width: "100%",
    paddingTop: "5px",
    paddingBottom: "5px",
    marginTop: "10px",
    marginBottom: "10px"
  }
};